<template>
  <div>
    <header
      ref="topnav"
      role="banner"
      :class="{
        'mainNavHidden': mainNavHidden,
        'ie11': isIE11,
        'search-active': searchActive,
        'close-search': closeSearch
      }"
      class="header-navigation-wrapper top-nav"
    >
      <a
        id="jump-to-header"
        href="#page-wrapper"
        class="button button--32 button--secondary skip-to-content"
      >
        Hoppa till sidans innehåll
      </a>
      <my-store
        v-if="showStore"
        :is-store="storeInformation.isStore"
        :store-data="storeData"
        @update-store="updateSelectedStore"
      />
      <div class="main-nav">
        <div
          ref="wrapcontainer"
          class="wrap-container"
        >
          <div class="row-noGutter-middle-noWrap">
            <navigation
              ref="navwrapper"
              :menu-items="navBarItems"
              :is-store="storeInformation.isStore"
              :store-name="storeData && storeData.storeName"
              :profile="storeInformation.profile"
              :store-url="storeInformation && storeInformation.storeUrl"
              :inline-menu-overflown="mainNavHidden"
              @initialWidth="initialWidth"
            />

            <div
              ref="separator"
              class="top-nav-separator"
            >
              <search-component
                v-if="renderSearch"
                ref="searchwrapper"
                :active="searchInitComplete"
                :style="searchInlineCssStyle"
                :class="{ 'is-initialized': searchInitComplete }"
                class="top-nav-search-component"
                @cancelsearchbar="cancelSearch"
              />
            </div>
            <div
              class="top-nav-right"
              :class="[renderClassNoEcommerce]"
            >
              <button
                type="button"
                aria-label="Sök på ica.se"
                aria-haspopup="true"
                class="button button--secondary button--32 button--icon button--search"
                @click="openSearch"
              >
                <svgicon
                  name="Navigation/Search"
                  class="search-icon"
                />
              </button>
              <avatar-dropdown :data="avatar" />
              <a
                v-if="onlineUrl"
                ref="onlineLink"
                :class="{
                  'button--secondary': !avatar.isLoggedIn,
                  'button--icon': showOnlyIconEcommerce,
                  'button--text-icon': !showOnlyIconEcommerce
                }"
                :href="(storeData && storeData.storeOnlineUrl) ? storeData.storeOnlineUrl : onlineUrl"
                class="button button--32 js-track-ecommerce"
              >
                <svgicon name="Shop/ShoppingCart" />E-handla
              </a>
            </div>
          </div>
        </div>
      </div>
      <sub-navigation
        v-if="subNavBarItems.length"
        :menu-items="subNavBarItems"
      />

      <transition-group
        v-if="alertBanners.length"
        name="alert"
        tag="div"
        class="alert-banners-wrapper"
        @before-leave="beforeLeave"
        @after-leave="afterLeave"
        @leave="leave"
      >
        <alert-banner
          v-for="item in filteredAlerts"
          :key="item.id"
          ref="alert"
          v-bind="item"
          @close-alert="closeAlert"
        />
      </transition-group>
    </header>
  </div>
</template>

<script>
//import throttle from 'lodash.throttle'
import debounce from "lodash.debounce"
import track from "@/analytics/dataLayerTopNav"
import myStore from "./subcomponents/my-store"
import Navigation from "./subcomponents/navigation"
import search from "./subcomponents/search/search"
import Avatar from "./subcomponents/avatar-dropdown"
import SubNavigation from "./subcomponents/sub-navigation"
import ResizeObserver from "@/utils/resizeObserver"
import IcaAlertBanner from "~/@ica/ica-molecules-vue/src/components/AlertBanner.vue"
import "#/Navigation/Search"
import "#/Shop/ShoppingCart"
import "#/Climate/ClimateGoals"
import "#/Time/WeeklyPlan"
import "#/Shop/ShoppingList"
import "#/Cooking/Recipe"
import "#/Controls/Settings"
import { setCookie, getCookie } from "@/utils/cookies.js"

const DEFAULT_SEARCH_OFFSET = "100%"

export default {
  name: "TopNav",
  components: {
    "my-store": myStore,
    Navigation,
    "search-component": search,
    "avatar-dropdown": Avatar,
    "sub-navigation": SubNavigation,
    "alert-banner": IcaAlertBanner
  },
  props: {
    name: {
      type: String,
      default: ""
    },
    lastVisitedStore: {
      type: Object,
      default () {
        return null
      }
    },
    navBarItems: {
      type: Array,
      default () {
        return []
      }
    },
    subNavBarItems: {
      type: Array,
      default () {
        return []
      }
    },
    avatar: {
      type: Object,
      default () {
        return {}
      }
    },
    storeInformation: {
      type: Object,
      default () {
        return {}
      }
    },
    onlineUrl: {
      type: String,
      default: ""
    },
    alertBanners: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      mainNavHidden: false,
      searchActive: false,
      renderSearch: false,
      searchInitComplete: false,
      inlineMenuLastKnownWidth: 0,
      searchFieldWidth: 0,
      isIE11: false,
      inlineSearchLeftOffset: 0,
      currentLeftOffset: "0px",
      closeSearch: false,
      initialNavigationWidth: null,
      showAlertBanners: this.alertBanners,
      savedAlerts: [],
      rafCount: 0,
      storeData: {},
      showStore: false,
      errors: []
    }
  },
  computed: {
    isMobileSearch () {
      return this.$root.currentSizeLte("md")
    },
    showOnlyIconEcommerce () {
      return (
        this.$root.currentSizeLte("xs") ||
        (this.$root.currentSizeLte("xs") && !this.avatar.isLoggedIn)
      )
    },
    smallSizeTracker () {
      return this.$root.currentSizeLte("sm")
    },
    searchInlineCssStyle () {
      let left = this.isMobileSearch ? "0" : this.currentLeftOffset
      return {
        left
      }
    },
    renderClassNoEcommerce () {
      return !this.onlineUrl ? "no-ecommerce" : ""
    },
    filteredAlerts () {
      return this.showAlertBanners.filter(
        item => !this.savedAlerts.includes(item.id)
      )
    }
  },
  watch: {
    isMobileSearch (isMobileSearch) {
      this.updateTracking()
      this.updateTopPadding()
      if (isMobileSearch && this.searchActive) {
        document.documentElement.classList.add("no-scroll")
      } else if (!isMobileSearch && this.searchActive) {
        document.documentElement.classList.remove("no-scroll")
      }
    },
    showOnlyIconEcommerce () {
      this.updateTracking()
      this.updateTopPadding()
    },
    smallSizeTracker () {
      this.updateTracking()
      this.updateTopPadding()
    },
    mainNavHidden () {
      this.updateTracking()
      this.updateTopPadding()
    },
    storeData (newVal) {
      if (newVal && newVal.MarketingName) {
        this.storeData.storeName = newVal.MarketingName
      }
       this.showStore = true
     this.handleResize()
    },
    showStore () {
      this.handleResize()
    }
  },
  beforeDestroy () {
    ResizeObserver.removeAllCallbacks(this)
  },
  mounted () {
    this.$nextTick().then(() => {
      this.handleResize()
    })
    ResizeObserver.addCallback(this, this.handleResize)

    this.isIE11 = !!window.MSInputMethodContext && !!document.documentMode

    this.setStoreFromCookie()
  },
  beforeMount () {
    this.getCookieValue()
  },
  methods: {
    updateTopPadding () {
      if (this.$refs.topnav) {
        this.$nextTick().then(() => {
          const height = Array.from(this.$refs.topnav.children).reduce(
            (sum, element) =>
              (sum +=
                element.id === "jump-to-header" ? 0 : element.offsetHeight),
            0
          )
          document.body.style.paddingTop = `${height}px`
        })
      }
    },
    dynamicHeightAlert () {
      let alerts = this.$refs.alert
      if (alerts) {
        this.$nextTick().then(() => {
          alerts.forEach(alert => {
            let el = alert.$el
            el.style.height = "auto"
            el.style.height = `${el.scrollHeight}px`
          })
        })
      }
    },
    updatePaddingRaf () {
      this.updateTopPadding()
      if (this.rafCount) {
        requestAnimationFrame(this.updatePaddingRaf)
      }
    },
    beforeLeave () {
      this.rafCount++
      this.updatePaddingRaf()
    },
    afterLeave () {
      this.rafCount--
      this.updateTopPadding()
    },
    leave (el) {
      el.style.height = "0"
    },
    initialWidth (width) {
      this.$data.initialNavigationWidth = width
      this.handleResize()
    },
    handleResize () {
      this.updateTopPadding()
      this.dynamicHeightAlert()
      const navElement = this.$refs.navwrapper.$el
      let elWidth = navElement.scrollWidth + this.$refs.separator.offsetWidth

      if (this.$data.initialNavigationWidth > elWidth) {
        this.$data.mainNavHidden = true
      } else {
        this.$data.mainNavHidden = false
      }

      const navWidth =
        navElement.scrollWidth - navElement.children[0].scrollWidth
      this.inlineSearchLeftOffset = navWidth

      if (this.currentLeftOffset !== DEFAULT_SEARCH_OFFSET) {
        this.currentLeftOffset = `-${this.inlineSearchLeftOffset}px`
      }

      // This is a fix for IE11 that fails on some initial flex-calculations
      // We force a reflow of the document on first load and everything is fine after that.
      /*setTimeout( () => {
              this.isIE11 = !!window.MSInputMethodContext && !!document.documentMode
       }, 0)*/
    },
    openSearch () {
      track.openedSearch()
      this.handleResize()
      this.$nextTick(() => {
        this.renderSearch = true
        this.currentLeftOffset = DEFAULT_SEARCH_OFFSET

        setTimeout(() => {
          this.searchActive = true
          this.currentLeftOffset = `-${this.inlineSearchLeftOffset}px`
        }, 100)

        const searchInitCompleteDelay = this.isMobileSearch ? 0 : 500

        setTimeout(() => {
          this.searchInitComplete = true
        }, searchInitCompleteDelay)

        if (this.isMobileSearch) {
          this.$nextTick().then(() => {
            this.$refs.separator.querySelector("[type=search]").focus()
          })
          document.documentElement.classList.add("no-scroll")
        }
      })
    },
    cancelSearch () {
      this.closeSearch = true

      setTimeout(() => {
        this.currentLeftOffset = "100%"
        this.searchActive = false
        this.renderSearch = false
        this.searchInitComplete = false
        document.documentElement.classList.remove("no-scroll")
        this.closeSearch = false
      }, 200)
    },

    closeAlert (id) {
      this.savedAlerts.push(id)
      this.showAlertBanners = this.filteredAlerts
      setCookie("alert-banner-enabled", this.savedAlerts.join(","), 365)
    },
    getCookieValue () {
      let getCookieValue = getCookie("alert-banner-enabled")
      if (getCookieValue) {
        const ids = getCookieValue.split(",")
        ids.forEach(item => {
          this.savedAlerts.push(item)
        })
      }
    },
    setStoreFromCookie () {
      
      const storeCookie = getCookie("store-cookie")
      const parsedStoreCookie = storeCookie ? JSON.parse(decodeURIComponent(storeCookie)) : null 

      if (this.storeInformation.isStore) {

        this.showStore = true
        let storeSlug = ''

        this.storeData.storeId = this.storeInformation.storeId.length >= 5 ? this.storeInformation.storeId : "00000".substring(this.storeInformation.storeId.toString().length, 5) + this.storeInformation.storeId
        this.storeData.storeName = this.storeInformation.storeName

        if (this.onlineUrl) {
          let splitedUrl = this.onlineUrl.split('/')
          storeSlug = splitedUrl[this.onlineUrl.indexOf('handla') + 1]
          this.setOnlineStore()
        }

        this.storeData.storeSeoUrl = storeSlug
        this.setNewStoreCookie()
      }
      else if (parsedStoreCookie) {
        const searchId = parseInt(parsedStoreCookie.storeId) 
        fetch(`/api/store/searchbyid?query=${searchId}`)
        .then(response => response.json())
        .then(data => data.Stats.TotalHits && this.updateSelectedStore(data.Documents[0], true) || this.throwError('StoreId did not match a store'))
        .catch(err => this.throwError(err))

        track.selectedStore(parsedStoreCookie)
      }
      else {
        this.showStore = true
        track.selectedStore(false)
      }
    },
    throwError (error) {
      this.errors.push(error)
      this.showStore = true
    },
    updateSelectedStore (store, cookieStore = false) {
      this.storeData = {}
      let storeId = store.Id.toString()
      this.storeData.storeName = store.MarketingName
      this.storeData.storeId = storeId.length >= 5 ? storeId : "00000".substring(storeId.toString().length, 5) + storeId
      this.storeData.LinkToWeb = store.LinkToWeb

    let storeUrl = store.Urls.filter(link => link.Type == "e-handel")
    let storeSlug = '' 

     if ((cookieStore == false) && storeUrl.length && storeUrl[0].Url) {
        let splitedUrl = storeUrl[0].Url.split('/')
        storeSlug = splitedUrl[this.onlineUrl.indexOf('handla') + 1]

        this.setOnlineStore()
      }


      let cookieInfo = {
        storeName: store.MarketingName,
        storeId: this.storeData.storeId.toString(),
        storeSeoUrl: storeSlug
      }

      setCookie("store-cookie", encodeURIComponent(JSON.stringify(cookieInfo)), 365, this.$root.envConfig.cookieSubDomain)
    },
    setNewStoreCookie () {
     
      let cookieInfo =  {
        storeName: this.storeData.storeName,
        storeId: this.storeData.storeId.toString(),
        storeSeoUrl: this.storeData.storeSeoUrl
      }
      setCookie("store-cookie", encodeURIComponent(JSON.stringify(cookieInfo)), 365,  this.$root.envConfig.cookieSubDomain)
    },
    setOnlineStore () {
      fetch(`/handla/browse/handleSelectStore.jsp?storeId=${this.storeData.storeId}`)
    },
    updateTracking: debounce(function () {
      if (!this.$data.mainNavHidden) {
        track.topNavLayout("x-large")
      } else if (!this.isMobileSearch) {
        track.topNavLayout("large")
      } else if (!this.smallSizeTracker) {
        track.topNavLayout("medium")
      } else if (!this.showOnlyIconEcommerce) {
        track.topNavLayout("small")
      } else {
        track.topNavLayout("x-small")
      }
    }, 50)
  }
}
</script>

<style lang="scss">
.top-nav {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 101;
  background-color: $color-white;

  @media print {
    display: none;
  }

  .skip-to-content {
    position: absolute;
    top: -999rem;
    left: 0.5rem;
    z-index: 4;

    &:focus {
      top: 0.5rem;
    }
  }

  .top-nav-search-component {
    position: absolute;
    top: -999em;
    opacity: 0;
    width: auto;

    .search-component {
      padding-left: 0;
      padding-right: 0;
    }
  }

  &.search-active {
    .main-nav > .wrap-container {
      padding: 0;

      @include media-breakpoint-up(lg) {
        padding-left: 1.5rem;
        padding-right: 1.5rem;
      }
    }

    .top-nav-search-component {
      display: block;
      height: 100vh;
      opacity: 1;
      position: fixed;
      top: 0;
      right: 0;
      padding-right: 0;

      @include media-breakpoint-up(lg) {
        height: auto;
        max-width: none;
        padding-left: 0.5rem;
        padding-right: 0.5rem;
        position: absolute;
        left: 100%;
        right: -2.5rem;
        top: -2rem;
        transition: left 200ms ease-in-out, opacity 400ms ease-in-out;
      }

      &.is-initialized {
        transition: none;
      }
    }

    &.close-search {
      .top-nav-search-component {
        @include media-breakpoint-up(lg) {
          transition: left 200ms ease-in-out, opacity 200ms ease-in-out;
          left: 100% !important;
          opacity: 0;
        }
      }
    }
  }

  .top-nav-separator {
    flex-grow: 1;
    position: relative;
  }
}

.main-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  position: relative;
  z-index: 3;
  background: $color-white;
  border-bottom: 0.0625rem solid $color-grey-special;
}

.top-nav-right {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 0;
  position: relative;
  width: calc(100% - 7rem);

  @include media-breakpoint-up(md) {
    width: auto;
  }

  > :not(:last-child) {
    margin-right: 0.5rem;
  }

  .button {
    white-space: nowrap;

    &--search {
      user-select: none;
      -webkit-tap-highlight-color: transparent;
      border: 0.0625rem solid transparent;
      transition: all 200ms ease;

      .svg-icon {
        margin-top: -1px;
        margin-left: -1px;
      }

      .top-nav.search-active &,
      &.active {
        background-color: $color-white;
        border: 0.0625rem solid $color-grey-special;
        border-radius: 0.25rem;
        fill: $color-grey-special;
        height: 2.5rem;
        margin-bottom: -0.25rem;
        margin-top: -0.25rem;
        transition: all 200ms ease;
      }
    }
  }

  &.no-ecommerce {
    .avatar-dropdown-placeholder {
      @include media-breakpoint-up(md) {
        left: auto;
        right: 0;
      }
    }
  }
}

.main-nav .nav-wrapper,
.main-nav .top-nav-right,
.my-store,
.subnav-desktop {
  .top-nav & {
    opacity: 1;
    transition: opacity 1.6s 0.1s;
  }

  .top-nav.search-active & {
    opacity: 0;

    /*
    visibility: hidden;
    position: absolute;
    top: -999999px;
    max-width: 100%;
    */

    @include media-breakpoint-up(lg) {
      position: relative;
      top: auto;
      opacity: 1;
      visibility: visible;
    }
  }
}

.alert-banners-wrapper {
  position: absolute;
  width: 100%;

  .alert-leave-active {
    transition: all 0.2s ease-in-out;
    transform: translateY(-2.8125rem);
  }
}
</style>
